import React, { useMemo, useContext } from "react";
import L from "leaflet";
import { Marker, Popup } from "react-leaflet";
import {
  mapCoordinate,
  convertReactComponentToHtmlIcon,
  formatName,
  setBlockStorageSize,
  setVerticalBlockStorageSize,
  countBlocksUsingLPNs,
  rotateMarker,
  GenerateHTMLBlockIcon
} from "../../util/helper/helperFunctions";
import { AppContext } from "../../Context/Context";
import { useDispatch } from "react-redux";
import { setSelectedBlockRackLocationData } from "../../store/plantSlice";

const RenderBlockStorage = ({ plantConfig, blockData, mapObject }) => {
  const {
    setIsBlockRackModalOpen,
    setIsRackModal
  } = useContext(AppContext);

  const dispatch = useDispatch();

  const getLocationGroupOffset = (convertedValue, offsetObject) => {
    if (convertedValue && offsetObject) {
      //Offset Object
      let offsetCoordinates = {
        x: convertedValue.lat + offsetObject.x,
        y: convertedValue.lng + offsetObject.y
      };
      //Offset Array
      let offsetCoordinateArray = [offsetCoordinates.x, offsetCoordinates.y];
      return offsetCoordinateArray;
    }
  };

  const getMetaDataOfLocation = (location, type) => {
    if (type === "block") {
      return {
        latitude: location.latitude,
        longitude: location.longitude,
        location: location.location,
        totalPositions: location.totalPositions,
        positionTransition: location.positionTransition,
        positionOrientation: location.positionOrientation,
        locationOrientation: location.locationOrientation,
        positionLength: location.positionLength
      };
    } else {
      return {
        latitude: location.latitude,
        longitude: location.longitude,
        location: location.location,
        totalPositions: location.totalPositions,
        locationOrientation: location.locationOrientation,
        positionLength: location.positionLength
      };
    }
  };

  const isBlockObjectValid = (obj) => {
    // Check if the object and index exist
    if (obj) {
      let firstLocationPalletIndex = Object.keys(obj)[0];
      let firstObjectPalletValue = obj[firstLocationPalletIndex];

      let firstPalletLevelIndex = Object.keys(firstObjectPalletValue)[0];
      let firstPalletLevelValue =
        firstObjectPalletValue[firstPalletLevelIndex][0];

      if (firstPalletLevelValue !== undefined) {
        return firstPalletLevelValue;
      } else {
        return null;
      }
    } else {
      return null;
    }
  };

  const formatBlockDataStructure = (blockData, type) => {
    /**
     * Group all the locations with key as location name
     */
    let locationGroup = {};
    blockData.forEach((location) => {
      let name;
      if (type === "rack") {
        name = formatName(location.location);
      } else {
        name = location.location;
      }
      if (!locationGroup[name]) {
        locationGroup[name] = [];
      }
      locationGroup[name].push(location);
    });

    if (type === "block") {
      /**
       * Group all the pallets inside location with key as palletPosition as key
       */
      for (let location in locationGroup) {
        let palletGroup = {};
        let currentLocationIterated = locationGroup[location];
        currentLocationIterated.forEach((palletSet) => {
          let pallet = palletSet.palletPosition;
          if (!palletGroup[pallet]) {
            palletGroup[pallet] = [];
          }
          palletGroup[pallet].push(palletSet);
        });
        locationGroup[location] = palletGroup;

        /**
         * Group all the Levels inside Pallets with key as Pallet Level
         */
        for (let pallet in palletGroup) {
          let levelGroup = {};
          palletGroup[pallet].forEach((levelSet) => {
            let level = levelSet.row;
            if (!levelGroup[level]) {
              levelGroup[level] = [];
            }
            levelGroup[level].push(levelSet);
          });
          palletGroup[pallet] = levelGroup;
        }
      }
    }
    return locationGroup;
  };
  const blockPalletClickHandler = (blockData) => {
    setIsBlockRackModalOpen(true);
    dispatch(setSelectedBlockRackLocationData(blockData));
    setIsRackModal(false);
  };

  const renderBlockMarkers = useMemo(() => {
    if (plantConfig && mapObject && blockData && blockData.length !== 0) {
      //   let blockData = blockStorageData;
      let blocksCount = countBlocksUsingLPNs(blockData);
      let formattedBlockData = formatBlockDataStructure(blockData, "block");

      formattedBlockData = Object.values(formattedBlockData);

      return formattedBlockData.map((block, index) => {
        let blockObject = Object.values(block);
        let validateObject = isBlockObjectValid(blockObject);
        if (validateObject) {
          // Metadata of the Location
          let metaData = validateObject;
          let {
            latitude,
            longitude,
            location,
            locationOrientation,
            positionLength
          } = getMetaDataOfLocation(metaData, "block");

          // Get Block Details from Config
          const { blockStorage } = plantConfig.indoorMap;
          const {
            marker,
            verticalMarker,
            bounds,
            eachItemOffset,
            eastLocationOffset,
            westLocationOffset
          } = blockStorage;

          // New Algorithm Start
          let convertedStart;
          let convertedPoints;
          let convertedLatLng;
          let convertedPositionObj;

          if (locationOrientation === "E") {
            // East or North Location Blocks
            convertedStart = mapCoordinate(
              longitude - positionLength,
              latitude,
              "block",
              bounds
            );
            convertedPoints = L.point(convertedStart.x, convertedStart.y);
            convertedLatLng =
              mapObject.map.target.layerPointToLatLng(convertedPoints);
            convertedPositionObj = getLocationGroupOffset(
              convertedLatLng,
              eachItemOffset[location]
                ? eachItemOffset[location]
                : eastLocationOffset
            );
          } else {
            //West or South Location Blocks
            convertedStart = mapCoordinate(
              longitude,
              latitude,
              "block",
              bounds
            );
            convertedPoints = L.point(convertedStart.x, convertedStart.y);
            convertedLatLng =
              mapObject.map.target.layerPointToLatLng(convertedPoints);
            convertedPositionObj = eachItemOffset[location]
              ? getLocationGroupOffset(
                  convertedLatLng,
                  eachItemOffset[location]
                )
              : getLocationGroupOffset(convertedLatLng, westLocationOffset);
          }

          const blockSizeConfig = {
            blockWidth: marker.size.width,
            blockHeight: marker.size.height,
            singleStackWidth: marker.singleStack.size.width,
            singleStackHeight: marker.singleStack.size.height
          };

          setBlockStorageSize(blockSizeConfig);
          if (verticalMarker) {
            const verticalBlockSizeConfig = {
              blockWidth: verticalMarker.size.width,
              blockHeight: verticalMarker.size.height,
              singleStackWidth: verticalMarker.singleStack.size.width,
              singleStackHeight: verticalMarker.singleStack.size.height
            };

            setVerticalBlockStorageSize(verticalBlockSizeConfig);
          }

          // Generate Block Icon with Data
          let reactIcon = convertReactComponentToHtmlIcon(
            <GenerateHTMLBlockIcon
              locationMetaData={metaData}
              locationsList={block}
              type="block"
              blocksCount={blocksCount}
              marker={marker}
            />
          );

          // Render Marker if Position Object is Defined
          if (convertedPositionObj !== undefined) {
            return (
              <Marker
                key={index}
                position={convertedPositionObj}
                icon={reactIcon}
                eventHandlers={{
                  click: () => blockPalletClickHandler(block)
                }}
                rotationAngle={rotateMarker(
                  metaData.angle,
                  metaData.locationOrientation,
                  metaData.positionOrientation
                )}
              >
                <Popup>{location}</Popup>
              </Marker>
            );
          }
        }
      });
    }
    // return null;
  }, [mapObject, plantConfig, blockData]);

  return renderBlockMarkers;
};

export default RenderBlockStorage;
